import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Inbox, Trash2, Package, FileText } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUS = [["new", "New"], ["processing", "Processing"], ["fulfilled", "Fulfilled"], ["cancelled", "Cancelled"]];
const STYLE = { new: "bg-amber-100 text-amber-700", processing: "bg-sky-100 text-sky-700", fulfilled: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-500" };

export default function Requests({ isAdmin }) {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => setReqs(await api("GET", "/reorder-requests")), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => { try { await api("PATCH", `/reorder-requests/${r.id}`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(e.message); } };
  const del = async (r) => { if (!window.confirm(`Delete reorder #${r.req_number}?`)) return; try { await api("DELETE", `/reorder-requests/${r.id}`); load(); } catch (e) { toast.error(e.message); } };
  const toQuote = async (r) => { try { const res = await api("POST", `/reorder-requests/${r.id}/convert-to-quote`); toast.success(`Quote #${res.quote.quote_number} created — open Quotes to price & send`); load(); } catch (e) { toast.error(e.message); } };

  const shown = reqs.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Customer reorder requests</h1>
        <span className="text-sm text-slate-400">{reqs.filter((r) => r.status === "new").length} new</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">Reorders customers sent from their portal. Service requests appear in <span className="font-semibold">Service &amp; Repairs</span> as booked work orders.</p>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[["all", "All"], ...STATUS].map(([v, l]) => <button key={v} onClick={() => setFilter(v)} data-testid={`req-filter-${v}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === v ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>)}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.length === 0 && <p className="text-sm text-slate-400 flex items-center gap-2"><Inbox className="h-4 w-4" /> No requests.</p>}
        {shown.map((r) => (
          <div key={r.id} data-testid="reorder-request-card" className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono2 font-bold text-slate-800">#{r.req_number}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${STYLE[r.status]}`}>{r.status}</span>
            </div>
            <p className="font-display font-bold text-slate-900 mt-1 truncate">{r.client_name}</p>
            <p className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString()}</p>
            <div className="mt-2 space-y-1">
              {(r.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><Package className="h-3.5 w-3.5 text-slate-400 shrink-0" /> <span className="flex-1 min-w-0 truncate">{it.name}</span> <span className="font-mono2 text-slate-500">×{it.qty}</span></div>
              ))}
            </div>
            {r.note && <p className="mt-2 text-xs text-slate-500 italic border-t border-slate-100 pt-2">“{r.note}”</p>}
            <div className="mt-3 flex items-center gap-2">
              <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid="reorder-status-select" className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-sky-400">
                {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {isAdmin && <button onClick={() => del(r)} data-testid="reorder-delete-btn" className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
            {r.converted_quote_id
              ? <p className="mt-2 text-[11px] text-indigo-500 inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Quote created</p>
              : <button onClick={() => toQuote(r)} data-testid="reorder-to-quote-btn" className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 transition-colors"><FileText className="h-3.5 w-3.5" /> Create quote from this</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
